import { faSnowflake, faArrowUpRightFromSquare, faCircleCheck } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { snowflake_urls } from "../../common/Constants";
import Button from "../../common/elements/Button";
import Icon from "../../common/elements/Icon";

// Default aggregation window passed to the Snowflake dashboard (matches the app's own default).
const DEFAULT_RANGE = "24h";

// Environments people can view. Each opens the dashboard pre-filtered to this experiment + env,
// so someone validating in DEV/QA sees that data and someone live in PROD sees PROD.
const ENVIRONMENTS = [
    { key: "PROD", label: "Production" },
    { key: "DEV", label: "Dev" },
    { key: "QA", label: "QA" },
];

// What the Snowflake dashboard shows — kept in sync with the legacy Real-Time Insights view.
const HIGHLIGHTS = [
    "Who's being bucketed, by hour (and into which variant)",
    "Whether users land in the split you configured",
    "Segment explorer — combine attributes (e.g. signed-in on web)",
    "Why users were skipped (audience, traffic, member checks)",
];

// NOTE: We intentionally do NOT iframe the Streamlit-in-Snowflake app here — app.snowflake.com
// blocks framing (X-Frame-Options / CSP frame-ancestors), so an embed renders blank. Instead we
// show a clear launch card. Once the app is deployed to a frame-friendly host (see the note in
// Constants: snowflake_urls.bucketDecisionsEmbedBase), this tab can render an <iframe src={
// snowflake_urls.bucketDecisionsEmbed(...)} /> instead of / above the card.
const BucketDecisionsTab = ({ experimentKey, range = DEFAULT_RANGE }) => {
    return (
        <>
            <div className="level mb-4">
                <div className="level-left">
                    <p className="subtitle is-5 mb-0">Bucket Decisions</p>
                </div>
            </div>

            <div className="box has-text-centered" style={{ maxWidth: "660px", margin: "2rem auto", padding: "2.5rem" }}>
                <span className="has-text-info" style={{ fontSize: "3.5rem", lineHeight: 1 }}>
                    <FontAwesomeIcon icon={faSnowflake} />
                </span>

                <p className="title is-4 mt-4 mb-2">Live bucketing decisions</p>
                <p className="has-text-grey mb-2">
                    Every time a user hits <strong>{experimentKey}</strong>, we record which variant they got —
                    or why they were skipped. This is that, near-real-time from Snowflake, pre-filtered to this experiment.
                </p>
                <p className="has-text-grey mb-5 is-size-7">
                    Available for every environment — check <strong>Dev</strong> or <strong>QA</strong> while you validate,
                    and <strong>Production</strong> once it's live.
                </p>

                <div className="content has-text-left is-inline-block mb-5">
                    {HIGHLIGHTS.map((h) => (
                        <p key={h} className="is-flex is-align-items-center mb-2 has-text-grey-dark">
                            <span className="has-text-info mr-2"><Icon icon={faCircleCheck} /></span>
                            <span>{h}</span>
                        </p>
                    ))}
                </div>

                <p className="heading has-text-grey mb-2">Open the dashboard for</p>
                <div className="buttons is-centered">
                    {ENVIRONMENTS.map((env, i) => (
                        <Button key={env.key}
                                className={i === 0 ? "is-info is-medium" : "is-info is-light"}
                                href={snowflake_urls.bucketDecisions(experimentKey, env.key, range)}
                                tooltip={`Open ${env.label} bucketing decisions in Snowflake (new tab)`}>
                            <Icon icon={faSnowflake} />
                            <span>{env.label}</span>
                            <Icon icon={faArrowUpRightFromSquare} />
                        </Button>
                    ))}
                </div>
                <p className="help has-text-grey mt-3">Opens in a new tab. Requires Snowflake access.</p>
            </div>
        </>
    );
};

export default BucketDecisionsTab;
